import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Layers, Search, Sparkles, BarChart3, Activity, Grid3X3, ArrowLeftRight } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { ClientOnly } from '../components/ClientOnly'
// useDataBus removed — rawHistory now reads from Zustand directly

// Lazy Load Charts to prevent SSR crashes
const MultiStrategyChart = React.lazy(() =>
    import('../components/charts/MultiStrategyChart').then((module) => ({ default: module.MultiStrategyChart })),
)
const CandleChart = React.lazy(() =>
    import('../components/charts/CandleChart').then((module) => ({ default: module.CandleChart })),
)
const RsiAnalytics = React.lazy(() =>
    import('../components/inspector/RsiAnalytics').then((module) => ({ default: module.RsiAnalytics })),
)
const StrikeCandleCharts = React.lazy(() =>
    import('../components/inspector/StrikeCandleCharts').then((module) => ({ default: module.StrikeCandleCharts })),
)

const ChartFallback = () => (
    <div className="w-full h-[300px] flex items-center justify-center bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
        <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs font-medium text-slate-400">Loading Chart...</span>
        </div>
    </div>
)

import { InspectorControls } from '../components/inspector/InspectorControls'
import { InspectorHistoryTable } from '../components/inspector/InspectorHistoryTable'
import { InspectorMatrix } from '../components/inspector/InspectorMatrix'
import { ItmOtmSection } from '../components/inspector/ItmOtmSection'
import { OpfsDebugTable } from '../components/inspector/OpfsDebugTable'
import { TargetSelection } from '../components/inspector/TargetSelection'
import { TinyTradeSignals } from '../components/inspector/TinyTradeSignals'
import type { InspectionMode, InspectionSource, InspectorColumnConfig } from '../components/inspector/types'
import { TradeIntelAnalytics } from '../components/TradeIntelAnalytics'
import { MultiStrategyTable } from '../components/tables/MultiStrategyTable'
import FullPageLoader from '../components/ui/FullPageLoader'
import { MaterialCard } from '../components/ui/MaterialCard'
import { INSPECTOR_COLUMNS } from '../config/columns'
import { getStrikeStepForSymbol } from '../config/constants'
import { useAIInsights } from '../hooks/useAIInsights'
import { useAnalysisHistory } from '../hooks/useAnalysisHistory'
import { useDashboardData } from '../hooks/useDashboardData'
import { usePageTitle } from '../hooks/usePageTitle'
import { useRsiSeries } from '../hooks/useRsiSeries'
import { useStrikeCandles } from '../hooks/useStrikeCandles'
import {
    analyzeExpandedHistory,
    calculateAnalysis,
    calculateStrategyMetrics,
    type StrategyType,
} from '../lib/analysis-utils'
import { getCurrentIST, isMarketHours } from '../lib/data-utils'
import { formatDateToYYYYMMDD, getLastTradingDay } from '../lib/date-utils'
import { calculateIntradayLevels, type IndicatorLevels } from '../lib/technical-indicators'
import { calculateTradeIntelligence } from '../lib/trade-intelligence'
import { fetchHistoricalCandles, type UpstoxCandle } from '../services/candle-service'
import { useSettingsStore } from '../stores/settingsStore'
import { AnalysisMode, IndexSymbol } from '../types'
import { useStrikeIndicators } from '../hooks/useStrikeIndicators'
import { useMarketStore } from '../services/store'

// 🚀 PERF FIX: Module-level stable empty array prevents infinite useSyncExternalStore loops.
// If this is inside the component, a new [] is created every render → Zustand thinks state changed → infinite loop.
const EMPTY_INSPECTOR_HISTORY: any[] = Object.freeze([]) as unknown as any[]

// Search Schema
const inspectorSearchSchema = z.object({
    symbol: z.nativeEnum(IndexSymbol).optional(),
    expiry: z.string().optional(),
    mode: z.nativeEnum(AnalysisMode).optional().default(AnalysisMode.OI),
    ceStrike: z.number().optional(),
    peStrike: z.number().optional(),
})

export const Route = createFileRoute('/_auth/inspector')({
    validateSearch: (search) => inspectorSearchSchema.parse(search),
    component: InspectorRoute,
})

function InspectorRoute() {
    const { symbol: searchSymbol, expiry: searchExpiry, ceStrike, peStrike } = Route.useSearch()
    // console.log( '[Inspector] Search Params:', { searchSymbol, searchExpiry, ceStrike, peStrike } )

    usePageTitle('Inspector')

    const {
        isLoading: loading,
        marketData,
        effectiveExpiry,
        analysis: liveAnalysis, // Rename to avoid conflict
    } = useDashboardData()
    const settingsSymbol = useSettingsStore((state) => state.symbol)
    const settingsExpiry = useSettingsStore((state) => state.expiry)
    const settingsMode = useSettingsStore((state) => state.mode)
    const settingsHistoricalDate = useSettingsStore((state) => state.historicalDate)
    const settingsBaseTime = useSettingsStore((state) => state.baseTime)
    const setSymbol = useSettingsStore((state) => state.setSymbol)
    const setExpiry = useSettingsStore((state) => state.setExpiry)

    // Sync search params with settings if needed
    useEffect(() => {
        if (searchSymbol && searchSymbol !== settingsSymbol) {
            // console.log( '[Inspector] Syncing Symbol from URL:', searchSymbol )
            setSymbol(searchSymbol)
        }
        if (searchExpiry && searchExpiry !== settingsExpiry) {
            // console.log( '[Inspector] Syncing Expiry from URL:', searchExpiry )
            setExpiry(searchExpiry)
        }
    }, [searchSymbol, searchExpiry, settingsSymbol, settingsExpiry, setSymbol, setExpiry])

    // --- State Management with Persistence ---
    const [mode, setMode] = useState<InspectionMode>(() => {
        if (typeof window !== 'undefined')
            return (localStorage.getItem('inspector_mode') as InspectionMode) || 'Multiple'
        return 'Multiple'
    })

    type InspectorTab = 'Signals' | 'Charts' | 'RSI' | 'Strategies' | 'Matrix' | 'ITM/OTM'
    const INSPECTOR_TABS = [
        { id: 'Signals', icon: Sparkles, label: 'Signals' },
        { id: 'Charts', icon: BarChart3, label: 'Charts' },
        { id: 'RSI', icon: Activity, label: 'RSI' },
        { id: 'Strategies', icon: Layers, label: 'Strategies' },
        { id: 'Matrix', icon: Grid3X3, label: 'Matrix' },
        { id: 'ITM/OTM', icon: ArrowLeftRight, label: 'ITM/OTM' },
    ] as const

    const [inspectorTab, setInspectorTab] = useState<InspectorTab>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('inspector_tab') as InspectorTab
            if (
                saved &&
                Object.values(INSPECTOR_TABS)
                    .map((t) => t.id)
                    .includes(saved)
            )
                return saved
        }
        return 'Signals'
    })

    useEffect(() => {
        if (typeof window !== 'undefined') localStorage.setItem('inspector_mode', mode)
    }, [mode])

    useEffect(() => {
        if (typeof window !== 'undefined') localStorage.setItem('inspector_tab', inspectorTab)
    }, [inspectorTab])

    const inspectionSource = useSettingsStore((state) => state.inspectionSource)
    const setInspectionSource = useSettingsStore((state) => state.setInspectionSource)

    // Strike Selection State - WITH PERSISTENCE (Symbol Aware)
    // Note: settings.symbol might be default on first render, but the Effect below will sync it.
    // However, fast-refresh might rely on this.
    const [selectedSingleStrike, setSelectedSingleStrike] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`inspector_${settingsSymbol}_single_strike`)
            if (saved) return parseInt(saved, 10) || 0
            // Fallback for legacy keys? No, better start fresh to avoid pollution.
        }
        return 0
    })

    const [selectedCeStrike, setSelectedCeStrike] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`inspector_${settingsSymbol}_ce_strike`)
            if (saved) return parseInt(saved, 10) || 0
        }
        return ceStrike || 0
    })

    const [selectedPeStrike, setSelectedPeStrike] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`inspector_${settingsSymbol}_pe_strike`)
            if (saved) return parseInt(saved, 10) || 0
        }
        return peStrike || 0
    })

    // Candle Chart State
    const [candleTimeframe, setCandleTimeframe] = useState('1minute')
    const [chartIndicators, setChartIndicators] = useState<IndicatorLevels | undefined>(undefined)

    // ========================================================================
    // Effect 1: Fetch Reference Indicator Levels (daily/weekly/monthly)
    // These are static for the trading session — only re-fetch when symbol changes
    // ========================================================================
    useEffect(() => {
        const loadIndicatorLevels = async () => {
            if (!settingsSymbol) return

            // Clear stale indicators immediately to prevent mismatch
            setChartIndicators(undefined)

            try {
                // Fetch daily, monthly, weekly in parallel — these rarely change
                const [dailyData, monthlyData, weeklyData] = await Promise.all([
                    fetchHistoricalCandles(settingsSymbol, 365, 'day'),
                    fetchHistoricalCandles(settingsSymbol, 365, 'month'),
                    fetchHistoricalCandles(settingsSymbol, 365, 'week'),
                ])

                const todayStr = new Date().toDateString()

                let prevDayRaw = null
                if (dailyData.length > 0) {
                    const lastDay = dailyData[dailyData.length - 1]
                    if (lastDay) {
                        if (new Date(lastDay.timestamp).toDateString() === todayStr) {
                            prevDayRaw = dailyData.length > 1 ? dailyData[dailyData.length - 2] : null
                        } else {
                            prevDayRaw = lastDay
                        }
                    }
                }
                const prevDay = prevDayRaw
                    ? { high: prevDayRaw.high, low: prevDayRaw.low, close: prevDayRaw.close }
                    : null

                let prevMonthRaw = null
                if (monthlyData.length > 0) {
                    const lastMonth = monthlyData[monthlyData.length - 1]
                    if (lastMonth) {
                        const isCurrentMonth =
                            new Date(lastMonth.timestamp).getMonth() === new Date().getMonth() &&
                            new Date(lastMonth.timestamp).getFullYear() === new Date().getFullYear()
                        if (isCurrentMonth) {
                            prevMonthRaw = monthlyData.length > 1 ? monthlyData[monthlyData.length - 2] : null
                        } else {
                            prevMonthRaw = lastMonth
                        }
                    }
                }
                const prevMonth = prevMonthRaw
                    ? { high: prevMonthRaw.high, low: prevMonthRaw.low, close: prevMonthRaw.close }
                    : null

                let prevWeekRaw = null
                if (weeklyData.length > 0) {
                    const lastWeek = weeklyData[weeklyData.length - 1]
                    if (lastWeek) {
                        const daysDiff =
                            (new Date().getTime() - new Date(lastWeek.timestamp).getTime()) / (1000 * 3600 * 24)
                        if (daysDiff < 7) {
                            prevWeekRaw = weeklyData.length > 1 ? weeklyData[weeklyData.length - 2] : null
                        } else {
                            prevWeekRaw = lastWeek
                        }
                    }
                }
                const prevWeek = prevWeekRaw
                    ? { high: prevWeekRaw.high, low: prevWeekRaw.low, close: prevWeekRaw.close }
                    : null

                // Intraday Levels (First 5m/15m of TODAY) — fetch 1 day of 1-min data
                const minuteData = await fetchHistoricalCandles(settingsSymbol, 1, '1minute')
                const todayCandles = minuteData.filter((c: any) => new Date(c.timestamp).toDateString() === todayStr)
                const intradayLevels = calculateIntradayLevels(todayCandles)

                setChartIndicators({
                    prevDay,
                    prevWeek,
                    prevMonth,
                    first5Min: intradayLevels.first5Min,
                    first15Min: intradayLevels.first15Min,
                })
            } catch (e) {
                console.error('[Inspector] Failed to load indicator levels:', e)
            }
        }

        loadIndicatorLevels()
    }, [settingsSymbol]) // Only re-fetch when symbol changes

    // ========================================================================
    // TanStack Query: Candle Data for Active Timeframe (replaces setInterval)
    // Auto-refreshes every 60s for 1minute timeframe; static for daily candles.
    // placeholderData keeps old candles visible during background refresh.
    // ========================================================================
    const isMinuteTimeframe = candleTimeframe === '1minute'
    const { data: candleData = [] } = useQuery<UpstoxCandle[]>({
        queryKey: ['inspector-candles', settingsSymbol, candleTimeframe],
        queryFn: async () => {
            if (!settingsSymbol) return []
            const isDaily = candleTimeframe === 'day'
            const daysToFetch = isDaily ? 365 : 10
            return fetchHistoricalCandles(settingsSymbol, daysToFetch, candleTimeframe)
        },
        enabled: !!settingsSymbol,
        refetchInterval: isMinuteTimeframe ? 60_000 : false,
        staleTime: isMinuteTimeframe ? 55_000 : 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    })

    const [selectedCustomCeStrikes, setSelectedCustomCeStrikes] = useState<number[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`inspector_${settingsSymbol}_custom_ce_strikes`)
            if (saved) {
                try {
                    return JSON.parse(saved)
                } catch (_) { console.warn('Ignored error in _auth.inspector.tsx:', _); }
            }
        }
        return []
    })
    const [selectedCustomPeStrikes, setSelectedCustomPeStrikes] = useState<number[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem(`inspector_${settingsSymbol}_custom_pe_strikes`)
            if (saved) {
                try {
                    return JSON.parse(saved)
                } catch (_) { console.warn('Ignored error in _auth.inspector.tsx:', _); }
            }
        }
        return []
    })

    // Track if user has manually changed strikes DURING THIS SESSION
    // Always starts false on refresh so defaults (MIN Strike) always apply
    const [userHasChangedStrikes, setUserHasChangedStrikes] = useState<boolean>(false)

    // Note: Strike selections are now persisted directly in their respective change handlers
    // instead of via useEffect to prevent race conditions during symbol switching.

    // RELOAD STATE ON SYMBOL CHANGE — Reset to 0 so MIN Strike default applies
    useEffect(() => {
        if (typeof window === 'undefined') return

        const sym = settingsSymbol

        const savedSingle = localStorage.getItem(`inspector_${sym}_single_strike`)
        setSelectedSingleStrike(savedSingle ? parseInt(savedSingle) : 0)

        // Reset CE/PE to 0 so the MIN Strike default effect will apply
        setSelectedCeStrike(0)
        setSelectedPeStrike(0)

        // Custom Lists
        setSelectedCustomCeStrikes([])
        setSelectedCustomPeStrikes([])

        // Always reset — defaults will be applied by the MIN Strike effect
        setUserHasChangedStrikes(false)
    }, [settingsSymbol])

    // Range State
    const rangeStartDate = useSettingsStore((state) => state.rangeStartDate)
    const setRangeStartDate = useSettingsStore((state) => state.setRangeStartDate)
    const rangeEndDate = useSettingsStore((state) => state.rangeEndDate)
    const setRangeEndDate = useSettingsStore((state) => state.setRangeEndDate)
    const historicalDate = useSettingsStore((state) => state.historicalDate)

    // Matrix State
    const [visibleColumns, setVisibleColumns] = useState<InspectorColumnConfig[]>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('inspector_columns')
            if (saved) {
                try {
                    const savedCols = JSON.parse(saved)
                    return INSPECTOR_COLUMNS.map((def: any) => {
                        const savedCol = savedCols.find((c: any) => c.id === def.id)
                        return savedCol ? { ...def, visible: savedCol.visible } : def
                    })
                } catch (_e) { console.warn('Ignored error in _auth.inspector.tsx:', _e); }
            }
        }
        return INSPECTOR_COLUMNS
    })

    useEffect(() => {
        if (typeof window !== 'undefined')
            localStorage.setItem(
                'inspector_columns',
                JSON.stringify(visibleColumns.map((c) => ({ id: c.id, visible: c.visible }))),
            )
    }, [visibleColumns])

    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('inspector_matrix_rows')
            return saved ? parseInt(saved, 10) : 15
        }
        return 15
    })

    useEffect(() => {
        if (typeof window !== 'undefined') localStorage.setItem('inspector_matrix_rows', rowsPerPage.toString())
    }, [rowsPerPage])

    // Time Gap
    const [timeGap, setTimeGap] = useState(() => {
        if (typeof window !== 'undefined') return parseInt(localStorage.getItem('inspector_time_gap') || '1', 10)
        return 1
    })

    useEffect(() => {
        if (typeof window !== 'undefined') localStorage.setItem('inspector_time_gap', timeGap.toString())
    }, [timeGap])

    const [rsiTimeFrame, setRsiTimeFrame] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('inspector_rsi_time_frame')
            if (saved) return parseInt(saved, 10)
        }
        return 1
    })

    useEffect(() => {
        if (typeof window !== 'undefined') localStorage.setItem('inspector_rsi_time_frame', rsiTimeFrame.toString())
    }, [rsiTimeFrame])

    const [rsiDays, setRsiDays] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('inspector_rsi_days')
            if (saved) return parseInt(saved, 10)
        }
        return 1
    })

    useEffect(() => {
        if (typeof window !== 'undefined') localStorage.setItem('inspector_rsi_days', rsiDays.toString())
    }, [rsiDays])

    // Strategy State
    const [strategyType, setStrategyType] = useState<StrategyType>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('inspector_strategy_type')
            if (saved) return saved as StrategyType
        }
        return 'Straddle'
    })
    useEffect(() => {
        if (typeof window !== 'undefined') localStorage.setItem('inspector_strategy_type', strategyType)
    }, [strategyType])

    const [strategyWidthStrokes, setStrategyWidthStrokes] = useState<number>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('inspector_strategy_width')
            if (saved) return parseInt(saved, 10)
        }
        return 2
    })
    useEffect(() => {
        if (typeof window !== 'undefined')
            localStorage.setItem('inspector_strategy_width', strategyWidthStrokes.toString())
    }, [strategyWidthStrokes])

    const [activeStrategyId, setActiveStrategyId] = useState<string | null>(() => {
        if (typeof window !== 'undefined') return localStorage.getItem('inspector_active_strategy')
        return null
    })
    useEffect(() => {
        if (typeof window !== 'undefined') {
            if (activeStrategyId) localStorage.setItem('inspector_active_strategy', activeStrategyId)
            else localStorage.removeItem('inspector_active_strategy')
        }
    }, [activeStrategyId])

    // Derived Selected Strikes
    const selectedStrikes = useMemo(() => {
        let strikes: number[] = []
        if (mode === 'Single') strikes = [selectedSingleStrike]
        else if (mode === 'Multiple') {
            strikes = [selectedCeStrike, selectedPeStrike]
        } else if (mode === 'Custom') {
            strikes = [...selectedCustomCeStrikes, ...selectedCustomPeStrikes]
        }

        // Fix Blank Data: Filter out 0s and sort
        const clean = [...new Set(strikes)].filter((s) => s > 0).sort((a, b) => a - b)
        return clean
    }, [
        mode,
        selectedSingleStrike,
        selectedCeStrike,
        selectedPeStrike,
        selectedCustomCeStrikes,
        selectedCustomPeStrikes,
    ])

    // Unified Analysis History
    const { data: analyzedHistory, isLoading: isHistoryLoading } = useAnalysisHistory({
        symbol: settingsSymbol,
        expiry: effectiveExpiry || '',
        range: 20,
        mode: settingsMode === AnalysisMode.VOLUME ? 'VOLUME' : 'OI',
        baseTimePattern: settingsBaseTime || undefined,
    })

    const history = useMemo(() => analyzedHistory || [], [analyzedHistory])


    // RAW History for Inspector Tables — reads from Zustand (where GlobalHistoryFetcher writes)
    const rawHistory = useMarketStore(useCallback((state) => {
        if (inspectionSource === 'CurrentIntraday') return state.intradayHistory?.[settingsSymbol] || EMPTY_INSPECTOR_HISTORY
        if (inspectionSource === 'HistoricalSnapshot') return state.historicalHistory?.[settingsSymbol] as any[] || EMPTY_INSPECTOR_HISTORY
        if (inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD') return state.rangeHistory?.[settingsSymbol] as any[] || EMPTY_INSPECTOR_HISTORY
        return EMPTY_INSPECTOR_HISTORY
    }, [settingsSymbol, inspectionSource]))

    useEffect(() => {
        console.log(`[_auth.inspector] rawHistory updated: ${rawHistory.length} items. Source: ${inspectionSource}, Symbol: ${settingsSymbol}`);
    }, [rawHistory, inspectionSource, settingsSymbol])

    // Data Derivation (Moved up for useStrikeCandles dependency)
    const activeMarketData = useMemo(() => {
        const parseRawSnapshot = (snap: any) => {
            if (!snap) return null
            if (snap.options) return snap // Already converted
            
            let extractedOptions = snap.rawOpts || []
            
            if (extractedOptions.length === 0 && snap.optionsData && snap.optionsData[settingsSymbol]) {
                const symbolData = snap.optionsData[settingsSymbol]
                if (symbolData[effectiveExpiry]) {
                    extractedOptions = symbolData[effectiveExpiry]
                } else {
                    const firstKey = Object.keys(symbolData)[0]
                    if (firstKey) extractedOptions = symbolData[firstKey] || []
                }
            }
            return {
                ...snap,
                options: extractedOptions,
            }
        }

        if (inspectionSource === 'CurrentIntraday') {
            // 1. Try Live Market Data (Preferred)
            if (marketData?.options?.length > 0) {
                return marketData
            }

            // 2. Fallback to History (Last Trading Day Replay)
            // Only if Live is empty/invalid (e.g. Weekend, Pre-market)
            if (rawHistory?.length > 0) {
                // Return the last available snapshot
                return parseRawSnapshot(rawHistory[rawHistory.length - 1])
            }

            return marketData
        }
        // For other modes (HistoricDay, DateRange), use history snapshots
        if (rawHistory?.length > 0) {
            return parseRawSnapshot(rawHistory[rawHistory.length - 1])
        }
        // Final fallback: live market data
        return marketData || null
    }, [inspectionSource, marketData, rawHistory, settingsSymbol, effectiveExpiry])

    // Strike Candles (New Integration)
    const { ceCandles, peCandles } = useStrikeCandles({
        selectedCeStrike,
        selectedPeStrike,
        // PASS LIVE MARKET DATA PRiORiTY: Historical snapshot data strips out option instrument keys.
        // We need live marketData to resolve keys for currently active options.
        activeMarketData:
            marketData?.options?.length || marketData?.optionChain?.length ? marketData : activeMarketData,
        timeframe: candleTimeframe,
        symbol: settingsSymbol,
    })

    // RSI Series (Updated with Candle Data)
    const rsiSeriesData = useRsiSeries({
        localHistory: history || [],
        mode,
        selectedSingleStrike,
        selectedCeStrike,
        selectedPeStrike,
        selectedCustomCeStrikes,
        selectedCustomPeStrikes,
        symbol: settingsSymbol,
        expiry: effectiveExpiry,
        rsiTimeFrame,
        rsiDays,
        ceCandles,
        peCandles,
        indexCandles: candleData,
    })

    // Available Strikes
    const availableStrikes: number[] = useMemo(() => {
        // USER REQUEST: Use Live Data for Dropdowns if History is delayed/empty
        // Priority 1: activeMarketData (which could be history or marketData depending on source)
        if (activeMarketData?.options && activeMarketData.options.length > 0) {
            return Array.from(new Set<number>(activeMarketData.options.map((o: any) => o.strike as number))).sort((a, b) => a - b)
        }

        // Priority 2: Fallback to Live MarketData (Global Store)
        // This ensures dropdowns are populated even if "Historic Day" data is loading or empty
        if (marketData?.options && marketData.options.length > 0) {
            return Array.from(new Set<number>(marketData.options.map((o: any) => o.strike as number))).sort((a, b) => a - b)
        }

        return []
    }, [activeMarketData, marketData])

    // Fallback Selection for Rendering (Prevents Blank Screen)
    const effectiveSelectedStrikes = useMemo(() => {
        if (selectedStrikes.length > 0) return selectedStrikes
        if (activeMarketData?.atmStrike) return [activeMarketData.atmStrike]
        return []
    }, [selectedStrikes, activeMarketData?.atmStrike])

    // Initialize/Sync Selection to S1/R1 (Default) or ATM
    useEffect(() => {
        // Safety: Only initialize specific strikes if they are currently at 0 (Reset condition)
        if (activeMarketData?.options?.length > 0) {
            // Try to find ATM from MarketData root or analysis
            const atm = activeMarketData.atmStrike || activeMarketData.analysis?.atmStrike || 0
            if (atm > 0) {
                if (selectedSingleStrike === 0) setSelectedSingleStrike(atm)
                if (selectedCeStrike === 0) setSelectedCeStrike(atm)
                if (selectedPeStrike === 0) setSelectedPeStrike(atm)
                if (selectedCustomCeStrikes.length === 0) setSelectedCustomCeStrikes([atm])
                if (selectedCustomPeStrikes.length === 0) setSelectedCustomPeStrikes([atm])
            }
        }
    }, [activeMarketData])

    // Analysis results from Unified History
    const analysis = useMemo(() => {
        if (!history || history.length === 0) return null
        return history[history.length - 1] || null
    }, [history])

    const normalizedAnalysis = useMemo(() => {
        if (!analysis) return null
        return {
            ...analysis,
            variance: {
                totalCount: analysis.totalCount,
                ceCount: analysis.ceCount,
                peCount: analysis.peCount,
                finalVariance: analysis.finalVariance,
                maxDiff: analysis.maxDiff,
                minDiff: analysis.minDiff,
                maxStrike: analysis.maxStrike,
                minStrike: analysis.minStrike,
            },
        }
    }, [analysis])

    // Base Analysis is index 0 or first snapshot if not found
    const baseAnalysis = useMemo(() => {
        if (!history || history.length === 0) return null
        return history[0] || null
    }, [history])

    const effectiveCeStrikeInit = selectedCeStrike || analysis?.resistanceStrike || 0
    const effectivePeStrikeInit = selectedPeStrike || analysis?.supportStrike || 0

    const { ceIndicators, peIndicators } = useStrikeIndicators({
        selectedCeStrike: effectiveCeStrikeInit,
        selectedPeStrike: effectivePeStrikeInit,
        activeMarketData,
        symbol: settingsSymbol,
        historicalDate,
    })

    // Smart Default Selection: Always reset to 09:16 MIN Strike on page load/refresh
    // User can change strikes freely during session, but refresh always resets to default
    useEffect(() => {
        if (typeof window === 'undefined') return // wait for client

        // Skip if user has manually changed strikes DURING THIS SESSION (not persisted across refreshes)
        if (userHasChangedStrikes) return

        // CRITICAL: Ensure data matches current symbol to prevent cross-contamination
        if (activeMarketData && activeMarketData.symbol !== settingsSymbol) return

        // USER REQUEST: Use 09:16 Min Strike as default for BOTH CE and PE legs
        const baseMinStrike = baseAnalysis?.minStrike || analysis?.minStrike
        
        if (baseMinStrike && baseMinStrike > 0) {
            const atm = activeMarketData?.atmStrike

            if (atm) {
                // Call (CE) Leg -> Base Min Strike
                if ((selectedCeStrike === 0 || selectedCeStrike === atm) && selectedCeStrike !== baseMinStrike) {
                    setSelectedCeStrike(baseMinStrike)
                    localStorage.setItem(`inspector_${settingsSymbol}_ce_strike`, String(baseMinStrike))
                }

                // Put (PE) Leg -> Base Min Strike (SAME as CE)
                if ((selectedPeStrike === 0 || selectedPeStrike === atm) && selectedPeStrike !== baseMinStrike) {
                    setSelectedPeStrike(baseMinStrike)
                    localStorage.setItem(`inspector_${settingsSymbol}_pe_strike`, String(baseMinStrike))
                }

                // Custom Arrays
                if (selectedCustomCeStrikes.length === 0) setSelectedCustomCeStrikes([baseMinStrike])
                if (selectedCustomPeStrikes.length === 0) setSelectedCustomPeStrikes([baseMinStrike])
            }
        }
    }, [
        analysis,
        baseAnalysis,
        activeMarketData?.atmStrike,
        activeMarketData?.symbol,
        userHasChangedStrikes,
        selectedCeStrike,
        selectedPeStrike,
        settingsSymbol,
        selectedCustomCeStrikes.length,
        selectedCustomPeStrikes.length,
    ])

    // Strategies
    const strategies = useMemo(() => {
        if (!activeMarketData?.options || effectiveSelectedStrikes.length === 0) return []
        const step = getStrikeStepForSymbol(settingsSymbol)
        const width = step * strategyWidthStrokes
        return calculateStrategyMetrics(activeMarketData.options, effectiveSelectedStrikes, strategyType, width)
    }, [activeMarketData, effectiveSelectedStrikes, strategyType, strategyWidthStrokes, settingsSymbol])

    useEffect(() => {
        if (strategies.length > 0) {
            const exists = strategies.find((s) => s.id === activeStrategyId)
            if (!exists) {
                setActiveStrategyId(strategies[0]!.id)
            }
        }
    }, [strategies, activeStrategyId])

    // AI Insights
    const {
        isOpen: showAiModal,
        response: aiResponse,
        generateInsights,
        closeAiModal,
        AiModalComponent,
    } = useAIInsights()

    // --- AI & SIGNALS INTEGRATION ---

    // 1. Prepare RSI Data for Signals
    const latestRsiData = useMemo(() => {
        const getLatestRsi = (labelSearch: string) => {
            const series = rsiSeriesData.find((s) => s.name.includes(labelSearch))
            if (series && series.data.length > 0) {
                // We use the last item in the *visible* series data, which respects rsiDays
                return series.data[series.data.length - 1]!.value
            }
            return 50
        }
        
        const data = {
            ceRsi: selectedCeStrike ? getLatestRsi('CE') : 50,
            peRsi: selectedPeStrike ? getLatestRsi('PE') : 50,
        }
        
        if (typeof window !== 'undefined') {
            localStorage.setItem(`inspector_${settingsSymbol}_rsi`, JSON.stringify(data))
        }
        
        return data
    }, [rsiSeriesData, selectedCeStrike, selectedPeStrike, settingsSymbol])

    // 2. Calculate Trade Signals Locally (Reuse existing data)
    const tradeSignal = useMemo(() => {
        if (!analysis || !history || history.length === 0) return null

        // Apply RSI Days selection filter to history
        let filteredHistory = history
        if (history.length > 0 && rsiDays) {
            // Find unique dates from newest to oldest
            const dates = Array.from(
                new Set(
                    history.map((h: any) => {
                        const ts = h?.timestampIST || h?.timestamp
                        return String(ts).split(/[T ]/)[0] // robust date extraction
                    }),
                ),
            ).reverse() // Newest first

            const cutoffDateStr = dates[Math.min(rsiDays, dates.length) - 1] // inclusive cutoff

            if (cutoffDateStr) {
                filteredHistory = history.filter((h: any) => {
                    const tsStr = String(h?.timestampIST || h?.timestamp)
                    const hDate = tsStr.split(/[T ]/)[0]
                    return hDate! >= cutoffDateStr // keep dates newer or equal to cutoff
                })
            }
        }

        return calculateTradeIntelligence({
            marketData: activeMarketData as any,
            analysis: analysis as any,
            mode: settingsMode,
            history: filteredHistory as any[],
            rsiData: latestRsiData,
            ceIndicators,
            peIndicators,
            ceStrikeLtp:
                (activeMarketData as any)?.options?.find(
                    (o: any) => o.strike === (selectedCeStrike || analysis.resistanceStrike),
                )?.callPrice || 0,
            peStrikeLtp:
                (activeMarketData as any)?.options?.find(
                    (o: any) => o.strike === (selectedPeStrike || analysis.supportStrike),
                )?.putPrice || 0,
        })
    }, [
        activeMarketData,
        analysis,
        history,
        settingsMode,
        latestRsiData,
        rsiDays,
        ceIndicators,
        peIndicators,
        selectedCeStrike,
        selectedPeStrike,
    ])

    // 3. Format Signals for AI
    const aiSignals = useMemo(() => {
        if (!tradeSignal) return []

        const mainSignal = {
            type: tradeSignal.signalType,
            message: `${tradeSignal.signalSubType}: ${tradeSignal.setup?.header || 'No Setup'}`,
            confidence: `${tradeSignal.signalStrength} (${tradeSignal.score})`,
        }

        // Add Thesis Points as secondary signals
        const thesisSignals = tradeSignal.thesisPoints.map((t) => ({
            type: t.type.toUpperCase(),
            message: t.text,
            confidence: 'Thesis',
        }))

        return [mainSignal, ...thesisSignals]
    }, [tradeSignal])

    const handleGenerateAiInsights = useCallback(async () => {
        if (!analysis || !marketData) return
        const activeStrategy = strategies.find((s) => s.id === activeStrategyId)
        generateInsights(
            analysis,
            marketData,
            { symbol: settingsSymbol, expiry: settingsExpiry || '', mode: settingsMode, range: 0 } as any,
            'StrikeInspector',
            { selectedStrikes, strategyType, activeStrategy },
            history,
            aiSignals, // <--- PASS SIGNALS HERE
        )
    }, [
        generateInsights,
        analysis,
        marketData,
        settingsSymbol,
        settingsExpiry,
        settingsMode,
        selectedStrikes,
        strategyType,
        strategies,
        activeStrategyId,
        history,
        aiSignals, // Add dependency
    ])

    const handleColumnToggle = useCallback((id: string) => {
        setVisibleColumns((prev) => prev.map((col) => (col.id === id ? { ...col, visible: !col.visible } : col)))
    }, [])

    // Stable Handlers for TargetSelection
    const handleSingleStrikeChange = useCallback(
        (v: number) => {
            setSelectedSingleStrike(v)
            if (typeof window !== 'undefined')
                localStorage.setItem(`inspector_${settingsSymbol}_single_strike`, String(v))
            setUserHasChangedStrikes(true)
            if (typeof window !== 'undefined')
                localStorage.setItem(`inspector_${settingsSymbol}_user_changed_strikes`, 'true')
        },
        [settingsSymbol],
    )

    const handleCeStrikeChange = useCallback(
        (v: number) => {
            setSelectedCeStrike(v)
            if (typeof window !== 'undefined') localStorage.setItem(`inspector_${settingsSymbol}_ce_strike`, String(v))
            setUserHasChangedStrikes(true)
            if (typeof window !== 'undefined')
                localStorage.setItem(`inspector_${settingsSymbol}_user_changed_strikes`, 'true')
        },
        [settingsSymbol],
    )

    const handlePeStrikeChange = useCallback(
        (v: number) => {
            setSelectedPeStrike(v)
            if (typeof window !== 'undefined') localStorage.setItem(`inspector_${settingsSymbol}_pe_strike`, String(v))
            setUserHasChangedStrikes(true)
            if (typeof window !== 'undefined')
                localStorage.setItem(`inspector_${settingsSymbol}_user_changed_strikes`, 'true')
        },
        [settingsSymbol],
    )

    const handleCustomCeStrikesWrapper = useCallback(
        (action: React.SetStateAction<number[]>) => {
            setSelectedCustomCeStrikes((prev) => {
                const next = typeof action === 'function' ? (action as any)(prev) : action
                if (typeof window !== 'undefined')
                    localStorage.setItem(`inspector_${settingsSymbol}_custom_ce_strikes`, JSON.stringify(next))
                return next
            })
            setUserHasChangedStrikes(true)
            if (typeof window !== 'undefined')
                localStorage.setItem(`inspector_${settingsSymbol}_user_changed_strikes`, 'true')
        },
        [settingsSymbol],
    )

    const handleCustomPeStrikesWrapper = useCallback(
        (action: React.SetStateAction<number[]>) => {
            setSelectedCustomPeStrikes((prev) => {
                const next = typeof action === 'function' ? (action as any)(prev) : action
                if (typeof window !== 'undefined')
                    localStorage.setItem(`inspector_${settingsSymbol}_custom_pe_strikes`, JSON.stringify(next))
                return next
            })
            setUserHasChangedStrikes(true)
            if (typeof window !== 'undefined')
                localStorage.setItem(`inspector_${settingsSymbol}_user_changed_strikes`, 'true')
        },
        [settingsSymbol],
    )

    // Matrix Pagination
    // FIX: analysis.tableRows is not populated in transformer. Use marketData.options or historySnapshot options directly.
    const rawMatrixRows = useMemo(() => {
        // Use rawHistory instead of activeMarketData or history which lack options structure.
        if (inspectionSource === 'CurrentIntraday' && marketData?.options?.length > 0) {
            return marketData.options
        } else if (rawHistory && rawHistory.length > 0) {
            const lastSnap = rawHistory[rawHistory.length - 1]
            if (lastSnap?.optionsData?.[settingsSymbol]) {
                const symbolData = lastSnap.optionsData[settingsSymbol]
                if (symbolData[effectiveExpiry]) return symbolData[effectiveExpiry]
                const firstKey = Object.keys(symbolData)[0]
                return firstKey ? symbolData[firstKey] : []
            }
        }
        return marketData?.options || []
    }, [marketData, rawHistory, inspectionSource, settingsSymbol, effectiveExpiry])

    // Matrix Data Construction (Decoupled Strikes)
    const activeMatrixRows = useMemo(() => {
        let computedRows: { ce?: any; pe?: any }[] = []

        // 1. If Single Mode, strict filter by selected strikes (standard behavior)
        if (mode === 'Single') {
            if (effectiveSelectedStrikes.length === 0) {
                computedRows = rawMatrixRows.map((row: any) => ({ ce: row, pe: row }))
            } else {
                const singleRows = rawMatrixRows.filter((row: any) => effectiveSelectedStrikes.includes(row.strike))
                computedRows = singleRows.map((row: any) => ({ ce: row, pe: row }))
            }
        } else {
            // 2. If Multiple (Dual) or Custom, create Zipped Rows
            let targetCeStrikes: number[] = []
            let targetPeStrikes: number[] = []

            if (mode === 'Multiple') {
                if (selectedCeStrike > 0) targetCeStrikes.push(selectedCeStrike)
                if (selectedPeStrike > 0) targetPeStrikes.push(selectedPeStrike)
            } else if (mode === 'Custom') {
                targetCeStrikes = [...selectedCustomCeStrikes].sort((a, b) => a - b)
                targetPeStrikes = [...selectedCustomPeStrikes].sort((a, b) => a - b)
            }

            const ceRows = targetCeStrikes
                .map((s) => rawMatrixRows.find((r: any) => r.strike === s))
                .filter((r: any) => !!r)

            const peRows = targetPeStrikes
                .map((s) => rawMatrixRows.find((r: any) => r.strike === s))
                .filter((r: any) => !!r)

            const maxLen = Math.max(ceRows.length, peRows.length)

            for (let i = 0; i < maxLen; i++) {
                const ce = ceRows[i]
                const pe = peRows[i]

                // Calculate Metrics
                const ceCallOI = ce ? ce.callOpenInterest || 0 : 0
                const cePutOI = ce ? ce.putOpenInterest || 0 : 0
                const ceCallVol = ce ? ce.callVolume || 0 : 0
                const cePutVol = ce ? ce.putVolume || 0 : 0

                const peCallOI = pe ? pe.callOpenInterest || 0 : 0
                const pePutOI = pe ? pe.putOpenInterest || 0 : 0
                const peCallVol = pe ? pe.callVolume || 0 : 0
                const pePutVol = pe ? pe.putVolume || 0 : 0

                // 1. Combined (Same Strike)
                const ceCombinedOI = ceCallOI + cePutOI
                const ceCombinedVol = ceCallVol + cePutVol
                const peCombinedOI = peCallOI + pePutOI
                const peCombinedVol = peCallVol + pePutVol

                // 2. Individual Strike Differences (Internal: PE - CE for that strike)
                const ceOIDiff = cePutOI - ceCallOI
                const ceVolDiff = cePutVol - ceCallVol
                const peOIDiff = pePutOI - peCallOI
                const peVolDiff = pePutVol - peCallVol

                // 3. Cross-Strike Differences (PE Strike Put - CE Strike Call)
                // Rule: OI Difference = including both strikes difference as PE Strike OI (-) CE Strike OI
                // Rule: Volume Difference = including both strikes difference as PE Strike Volume (-) CE Strike Volume
                const crossOIDiff = pePutOI - ceCallOI
                const crossVolDiff = pePutVol - ceCallVol

                computedRows.push({
                    ce: ce
                        ? {
                              ...ce,
                              ceCombinedOI,
                              ceCombinedVol,
                              peCombinedOI,
                              peCombinedVol,
                              ceOIDiff,
                              ceVolDiff,
                              peOIDiff,
                              peVolDiff,
                              crossOIDiff,
                              crossVolDiff,
                          }
                        : undefined,
                    pe: pe
                        ? {
                              ...pe,
                              ceCombinedOI,
                              ceCombinedVol,
                              peCombinedOI,
                              peCombinedVol,
                              ceOIDiff,
                              ceVolDiff,
                              peOIDiff,
                              peVolDiff,
                              crossOIDiff,
                              crossVolDiff,
                          }
                        : undefined,
                })
            }
        }

        return computedRows
    }, [
        mode,
        effectiveSelectedStrikes,
        selectedCeStrike,
        selectedPeStrike,
        selectedCustomCeStrikes,
        selectedCustomPeStrikes,
        rawMatrixRows,
    ])

    const matrixSplitRows = activeMatrixRows

    // Reset Defaults Handler
    const handleReset = useCallback(() => {
        setMode('Multiple')
        setInspectionSource('CurrentIntraday')
        setRangeStartDate(formatDateToYYYYMMDD(getLastTradingDay()))
        setRangeEndDate(formatDateToYYYYMMDD(getLastTradingDay()))

        if (typeof window !== 'undefined') {
            localStorage.removeItem(`inspector_${settingsSymbol}_single_strike`)
            localStorage.removeItem(`inspector_${settingsSymbol}_ce_strike`)
            localStorage.removeItem(`inspector_${settingsSymbol}_pe_strike`)
            localStorage.removeItem(`inspector_${settingsSymbol}_user_changed_strikes`)
            localStorage.removeItem(`inspector_${settingsSymbol}_custom_ce_strikes`)
            localStorage.removeItem(`inspector_${settingsSymbol}_custom_pe_strikes`)
            localStorage.removeItem('inspector_range_start')
            localStorage.removeItem('inspector_range_end')
            localStorage.removeItem('inspector_rsi_time_frame')
            localStorage.removeItem('inspector_rsi_days')
            localStorage.removeItem('inspector_strategy_type')
            localStorage.removeItem('inspector_strategy_width')
            localStorage.removeItem('inspector_active_strategy')
        }

        setUserHasChangedStrikes(false)
        setRsiTimeFrame(1)
        setRsiDays(1)

        // USER REQUEST: Reset to Base Min Strike for BOTH CE and PE
        const resetStrike = baseAnalysis?.minStrike || analysis?.minStrike

        if (resetStrike && resetStrike > 0 && activeMarketData?.symbol === settingsSymbol) {
            setSelectedCeStrike(resetStrike)
            setSelectedPeStrike(resetStrike)
            setSelectedCustomCeStrikes([resetStrike])
            setSelectedCustomPeStrikes([resetStrike])
        } else if (activeMarketData?.atmStrike && activeMarketData?.symbol === settingsSymbol) {
            setSelectedCeStrike(activeMarketData.atmStrike)
            setSelectedPeStrike(activeMarketData.atmStrike)
        } else {
            setSelectedSingleStrike(0)
            setSelectedCeStrike(0)
            setSelectedPeStrike(0)
        }
    }, [analysis, baseAnalysis, activeMarketData?.atmStrike, activeMarketData?.symbol, settingsSymbol])

    return (
        <React.Fragment>
            {showAiModal && (
                <AiModalComponent isOpen={showAiModal} onClose={closeAiModal} response={aiResponse} isLoading={false} />
            )}

            <div className="flex flex-col gap-4 p-4 md:p-4 max-w-[1920px] mx-auto animate-in fade-in duration-500">
                {/* Main Content Grid */}
                {/* USER REQUEST: Full Weight Cards (Full Width) */}
                <div className="grid grid-cols-1 gap-6">
                    {/* Charts & Primary Controls */}
                    <div className="flex flex-col gap-4">
                        {/* Controls */}
                        <div className="bg-white dark:bg-slate-900 rounded-[32px] p-1 shadow-2xl shadow-indigo-500/5 border border-white/60 dark:border-slate-800 transition-colors relative z-30">
                            <InspectorControls
                                mode={mode}
                                setMode={setMode}
                                inspectionSource={inspectionSource}
                                setInspectionSource={setInspectionSource}
                                rangeStartDate={rangeStartDate}
                                setRangeStartDate={setRangeStartDate}
                                rangeEndDate={rangeEndDate}
                                setRangeEndDate={setRangeEndDate}
                                onReset={handleReset}
                                baseAnalysis={baseAnalysis}
                            />
                        </div>

                        {/* Target Selection */}
                        <TargetSelection
                            mode={mode}
                            availableStrikes={availableStrikes}
                            selectedSingleStrike={selectedSingleStrike}
                            setSelectedSingleStrike={handleSingleStrikeChange}
                            selectedCeStrike={selectedCeStrike}
                            setSelectedCeStrike={handleCeStrikeChange}
                            selectedPeStrike={selectedPeStrike}
                            setSelectedPeStrike={handlePeStrikeChange}
                            selectedCustomCeStrikes={selectedCustomCeStrikes}
                            setSelectedCustomCeStrikes={handleCustomCeStrikesWrapper}
                            selectedCustomPeStrikes={selectedCustomPeStrikes}
                            setSelectedCustomPeStrikes={handleCustomPeStrikesWrapper}
                            analysis={normalizedAnalysis as any}
                            activeMarketData={activeMarketData}
                        />

                        {/* Tiny Signals Banner (Always Visible) */}
                        <div className="animate-in fade-in slide-in-from-top-4 duration-500 delay-100">
                            <TinyTradeSignals signal={tradeSignal} />
                        </div>

                        {/* Inspector Tab Switcher — Natural Document Flow */}
                        <div className="relative z-10 flex justify-center w-full mb-4">
                            <div className="flex bg-slate-100 dark:bg-slate-800 p-1.5 rounded-2xl ring-1 ring-inset ring-slate-200/50 dark:ring-slate-700/50 overflow-x-auto no-scrollbar max-w-full">
                                {INSPECTOR_TABS.map((tab) => {
                                    const isActive = inspectorTab === tab.id
                                    const Icon = tab.icon
                                    return (
                                        <button
                                            key={tab.id}
                                            onClick={() => setInspectorTab(tab.id as InspectorTab)}
                                            className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 min-w-max ${
                                                isActive
                                                    ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.1)] ring-1 ring-slate-200/50 dark:ring-slate-600'
                                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50'
                                            }`}
                                        >
                                            <Icon size={18} strokeWidth={2.5} />
                                            {tab.label}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>

                        {/* Trade Intelligence Card (Signals Tab) */}
                        <div
                            className={`relative z-0 bg-white dark:bg-slate-900 rounded-[32px] p-1 shadow-2xl shadow-indigo-500/5 border border-white/60 dark:border-slate-800 transition-colors animate-in fade-in slide-in-from-bottom-4 duration-500 ${inspectorTab === 'Signals' ? 'block' : 'hidden'}`}
                        >
                            <div className="p-4 md:p-6">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl">
                                        <Sparkles size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">
                                            Trade Intelligence
                                        </h3>
                                        <p className="text-sm text-slate-500 font-medium">
                                            AI-powered trend and momentum analysis
                                        </p>
                                    </div>
                                </div>

                                {normalizedAnalysis && activeMarketData ? (
                                    <TradeIntelAnalytics
                                        mode={settingsMode}
                                        range={20}
                                        history={history as any[]}
                                        rsiData={latestRsiData}
                                        marketData={activeMarketData as any}
                                        analysis={normalizedAnalysis as any}
                                        selectedCeStrike={selectedCeStrike || undefined}
                                        selectedPeStrike={selectedPeStrike || undefined}
                                        signal={tradeSignal}
                                    />
                                ) : (
                                    <div className="h-48 flex items-center justify-center text-slate-400 font-medium bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700">
                                        Waiting for analysis data...
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Main Candle Chart (Charts Tab) */}
                        <div
                            className={`relative z-0 bg-white dark:bg-slate-900 rounded-[32px] p-4 md:p-6 shadow-2xl shadow-indigo-500/5 border border-white/60 dark:border-slate-800 transition-colors min-h-[600px] animate-in fade-in slide-in-from-bottom-4 duration-500 ${inspectorTab === 'Charts' ? 'block' : 'hidden'}`}
                        >
                            <ClientOnly fallback={<ChartFallback />}>
                                <React.Suspense fallback={<ChartFallback />}>
                                    <CandleChart
                                        data={candleData}
                                        symbol={settingsSymbol}
                                        indicators={chartIndicators}
                                        activeTimeframe={candleTimeframe}
                                        onTimeframeChange={setCandleTimeframe}
                                        height={600}
                                        liveLtp={activeMarketData?.spotPrice || null}
                                    />
                                </React.Suspense>
                            </ClientOnly>

                            {/* Strike Charts (New) */}
                            {(mode === 'Single' || mode === 'Multiple') && (
                                <div className="mt-8 pt-8 border-t border-slate-100 dark:border-slate-800">
                                    <ClientOnly fallback={<ChartFallback />}>
                                        <React.Suspense fallback={<ChartFallback />}>
                                            <StrikeCandleCharts
                                                ceData={ceCandles}
                                                peData={peCandles}
                                                selectedCeStrike={
                                                    mode === 'Single' ? selectedSingleStrike : selectedCeStrike
                                                }
                                                selectedPeStrike={
                                                    mode === 'Single' ? selectedSingleStrike : selectedPeStrike
                                                }
                                                symbol={settingsSymbol}
                                                timeframe={candleTimeframe}
                                                onTimeframeChange={setCandleTimeframe}
                                                activeMarketData={activeMarketData as any}
                                            />
                                        </React.Suspense>
                                    </ClientOnly>
                                </div>
                            )}
                        </div>

                        {/* RSI Tab */}
                        <div
                            className={`relative z-0 bg-white dark:bg-slate-900 rounded-[32px] p-1 shadow-2xl shadow-indigo-500/5 border border-white/60 dark:border-slate-800 transition-colors animate-in fade-in slide-in-from-bottom-4 duration-500 ${inspectorTab === 'RSI' && history && history.length > 0 ? 'block' : 'hidden'}`}
                        >
                            <div className="p-4 md:p-6">
                                <ClientOnly fallback={<ChartFallback />}>
                                    <React.Suspense fallback={<ChartFallback />}>
                                        <RsiAnalytics
                                            rsiSeriesData={rsiSeriesData}
                                            selectedStrikes={effectiveSelectedStrikes}
                                            rsiTimeFrame={rsiTimeFrame}
                                            setRsiTimeFrame={setRsiTimeFrame}
                                            rsiDays={rsiDays}
                                            setRsiDays={setRsiDays}
                                            activeMarketData={activeMarketData}
                                            history={rawHistory || []}
                                            selectedCeStrike={selectedCeStrike}
                                            selectedPeStrike={selectedPeStrike}
                                            symbol={settingsSymbol}
                                        />
                                    </React.Suspense>
                                </ClientOnly>
                            </div>
                        </div>

                        {/* Inspector Matrix & History Table (Matrix Tab) */}
                        <div
                            className={`relative z-0 flex-col gap-12 animate-in fade-in slide-in-from-bottom-4 duration-500 ${inspectorTab === 'Matrix' ? 'flex' : 'hidden'}`}
                        >
                            <div className="bg-white dark:bg-slate-900 rounded-[32px] p-1 shadow-2xl shadow-indigo-500/5 border border-white/60 dark:border-slate-800 transition-colors overflow-hidden">
                                <InspectorMatrix
                                    columnConfigs={visibleColumns}
                                    handleColumnToggle={handleColumnToggle}
                                    rows={matrixSplitRows}
                                    visibleColumns={visibleColumns.filter((c) => c.visible)}
                                    totalItems={activeMatrixRows.length}
                                    analysis={analysis as any}
                                    mode={mode}
                                />
                            </div>

                            {/* History Table */}
                            <div className="bg-white dark:bg-slate-900 rounded-[24px] p-1 shadow-xl border border-white/60 dark:border-slate-800 transition-colors">
                                <InspectorHistoryTable
                                    symbol={settingsSymbol}
                                    expiry={effectiveExpiry}
                                    selectedStrikes={effectiveSelectedStrikes}
                                    rangeStartDate={rangeStartDate}
                                    rangeEndDate={rangeEndDate}
                                    inspectionSource={inspectionSource}
                                    inspectionMode={mode}
                                    isLive={inspectionSource === 'CurrentIntraday'}
                                    effectiveHistory={rawHistory as any[]}
                                    timeGap={timeGap}
                                    setTimeGap={setTimeGap}
                                    ceStrikes={mode === 'Multiple' ? [selectedCeStrike] : undefined}
                                    peStrikes={mode === 'Multiple' ? [selectedPeStrike] : undefined}
                                />
                            </div>
                        </div>

                        {/* ITM/OTM Tab */}
                        <div
                            className={`relative z-0 bg-white dark:bg-slate-900 rounded-[32px] p-4 md:p-6 shadow-2xl shadow-indigo-500/5 border border-white/60 dark:border-slate-800 transition-colors animate-in fade-in slide-in-from-bottom-4 duration-500 ${inspectorTab === 'ITM/OTM' ? 'block' : 'hidden'}`}
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded-2xl shadow-sm">
                                    <ArrowLeftRight size={24} strokeWidth={2.5} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">
                                        ITM / OTM Analysis
                                    </h3>
                                    <p className="text-sm font-medium text-slate-400 dark:text-slate-500">
                                        In-The-Money vs Out-The-Money aggregated history
                                    </p>
                                </div>
                            </div>
                            <ItmOtmSection
                                symbol={settingsSymbol}
                                expiry={effectiveExpiry || ''}
                                history={rawHistory || []}
                                rsiSeriesData={rsiSeriesData}
                                marketData={marketData}
                            />
                        </div>

                        {/* Multi-Strategy (Strategies Tab) */}
                        <div
                            className={`mb-0 shadow-2xl shadow-indigo-500/5 border-none bg-white dark:bg-slate-900 dark:border-slate-800 rounded-[32px] overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 ${inspectorTab === 'Strategies' ? 'block' : 'hidden'}`}
                        >
                            <MaterialCard className="relative z-0 w-full mb-0 shadow-none border-none">
                                <div className="px-4 md:px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex flex-wrap gap-4 justify-between items-center bg-white dark:bg-slate-900 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 rounded-2xl shadow-sm">
                                            <Layers size={24} strokeWidth={2.5} />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">
                                                Multi-Strategy Analyzer
                                            </h3>
                                            <p className="text-sm font-medium text-slate-400 dark:text-slate-500">
                                                Advanced payoff & risk modeling
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        {(strategyType === 'Strangle' || strategyType === 'IronFly') && (
                                            <div className="flex items-center gap-1 p-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg transition-colors">
                                                {[1, 2, 3, 4, 5].map((w) => (
                                                    <button
                                                        key={w}
                                                        onClick={() => setStrategyWidthStrokes(w)}
                                                        className={`px-2 py-1 text-[10px] font-bold rounded ${
                                                            strategyWidthStrokes === w
                                                                ? 'bg-indigo-600 text-white shadow-sm'
                                                                : 'text-slate-400 dark:text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
                                                        }`}
                                                        title={`${w} Strike${w > 1 ? 's' : ''} Wide`}
                                                    >
                                                        {w}x
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg transition-colors">
                                            {(['Straddle', 'Strangle', 'IronFly'] as StrategyType[]).map((t) => (
                                                <button
                                                    key={t}
                                                    onClick={() => setStrategyType(t)}
                                                    className={`px-3 py-1 text-[10px] font-bold uppercase rounded-md transition-all ${
                                                        strategyType === t
                                                            ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                                    }`}
                                                >
                                                    {t}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Chart Area */}
                                <div className="px-4 md:px-6 pt-0 pb-4 bg-white dark:bg-slate-900 transition-colors">
                                    {activeStrategyId &&
                                        strategies.find((s) => s.id === activeStrategyId) &&
                                        activeMarketData?.spotPrice && (
                                            <ClientOnly fallback={<ChartFallback />}>
                                                <React.Suspense fallback={<ChartFallback />}>
                                                    <MultiStrategyChart
                                                        strategy={strategies.find((s) => s.id === activeStrategyId)!}
                                                        spotPrice={activeMarketData.spotPrice || 0}
                                                    />
                                                </React.Suspense>
                                            </ClientOnly>
                                        )}
                                </div>

                                <div className="px-6 pb-8 bg-white dark:bg-slate-900 transition-colors rounded-b-[24px]">
                                    <MultiStrategyTable
                                        strategies={strategies}
                                        activeStrategyId={activeStrategyId}
                                        onSelectStrategy={setActiveStrategyId}
                                        isLoading={loading && !activeMarketData}
                                        supportStrike={normalizedAnalysis?.supportStrike}
                                        resistanceStrike={normalizedAnalysis?.resistanceStrike}
                                    />
                                </div>
                            </MaterialCard>
                        </div>
                    </div>
                </div>

                {/* OpfsDebugTable (Dev Mode Only or Hidden) */}
                {/* <div className="mt-8">
          <OpfsDebugTable
            files={[]}
            onDelete={async () => {}}
            refresh={loadOpfsDebugData}
          />
        </div> */}
            </div>

        </React.Fragment>
    )
}
